import React, { createContext, useContext } from 'react';
import { RestAPIEndpoints } from '@lifeomic/react-client';
import { ImpressionItem } from './ImpressionItem';
import { ViewMode } from '../Attachments/InlineAttachment';
import { useActiveUserId } from '../../../../../hooks';
import { apiHooks } from '../../../../../hooks/useRestAPIHooks';

type SendAgentFeedbackRequest =
  RestAPIEndpoints['POST /v1/agents-v2/feedback']['Request'];
type SendAgentFeedbackInput = {
  feedback: SendAgentFeedbackRequest['feedback'];
  agentName: SendAgentFeedbackRequest['agent_name'];
  traceId: SendAgentFeedbackRequest['trace_id'];
};

export type ImpressionEditorContextType = {
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  activeImpression: ImpressionItem;
  viewMode: ViewMode;
  patient: fhir.Patient & { id: string };
  refresh: () => void;
  sendAgentFeedback: (input: SendAgentFeedbackInput) => Promise<unknown>;
  activeUserId: string;
};

const ImpressionEditorContext = createContext<
  ImpressionEditorContextType | undefined
>(undefined);

export const ImpressionEditorProvider: React.FC<{
  patient: fhir.Patient & { id: string };
  children: React.ReactNode;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  activeImpression: ImpressionItem;
  viewMode: ViewMode;
  refresh: () => void;
}> = ({
  children,
  patient,
  isEditing,
  setIsEditing,
  activeImpression,
  viewMode,
  refresh,
}) => {
  const activeUserId = useActiveUserId();
  const sendFeedbackMutation = apiHooks.useAPIMutation(
    'POST /v1/agents-v2/feedback',
  );
  const sendAgentFeedback = React.useCallback(
    ({ feedback, agentName, traceId }: SendAgentFeedbackInput) =>
      sendFeedbackMutation.mutateAsync({
        feedback,
        agent_name: agentName,
        trace_id: traceId,
      }),
    [sendFeedbackMutation],
  );
  return (
    <ImpressionEditorContext.Provider
      value={{
        isEditing,
        activeImpression,
        setIsEditing,
        viewMode,
        patient,
        refresh,
        sendAgentFeedback,
        activeUserId,
      }}
    >
      {children}
    </ImpressionEditorContext.Provider>
  );
};

// Custom hook to use the ImpressionEditorContext
export const useImpressionEditorContext = (): ImpressionEditorContextType => {
  const context = useContext(ImpressionEditorContext);
  if (!context) {
    throw new Error(
      'useImpressionEditorContext must be used within an ImpressionEditorProvider',
    );
  }
  return context;
};
